<script setup>
import { ref, onMounted, computed } from "vue";
import { useRouter } from "vue-router";
import axios from 'axios';
import { BASE_URL, BASE_URL_2 } from '@/Config/config';
import { useStore } from 'vuex';
import Swal from 'sweetalert2';

const store = useStore();
const router = useRouter();

// Reactive variables
const isLoading = ref(false);
const error = ref(null);
const warningLetters = ref([]);
const trucks = ref([]);
const trailers = ref([]);
const drivers = ref([]);
const searchQuery = ref('');
const currentPage = ref(1);
const itemsPerPage = ref(10);
const sortField = ref('created_at');
const sortDirection = ref('desc');

// Alert functions
const showSuccessAlert = (message) => {
    Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: message,
        padding: '2em',
        timer: 3000,
        timerProgressBar: true,
        showConfirmButton: false
    });
};

const showErrorAlert = (message) => {
    Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: message,
        padding: '2em',
        confirmButtonColor: '#3085d6',
        confirmButtonText: 'OK'
    });
};

const showConfirmDialog = async (title, message) => {
    const result = await Swal.fire({
        title: title,
        text: message,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, proceed',
        cancelButtonText: 'Cancel'
    });
    return result.isConfirmed;
};

// Computed properties
const filteredLetters = computed(() => {
    let result = warningLetters.value;

    if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase();
        result = result.filter(letter =>
            (letter.employee_id && letter.employee_id.toLowerCase().includes(query)) ||
            (getDriverName(letter.employee_id) && getDriverName(letter.employee_id).toLowerCase().includes(query)) ||
            (letter.issued_by?.username && letter.issued_by.username.toLowerCase().includes(query))
        );
    }

    // Sorting
    result.sort((a, b) => {
        let fieldA, fieldB;

        if (sortField.value === 'driver') {
            fieldA = getDriverName(a.employee_id);
            fieldB = getDriverName(b.employee_id);
        } else {
            fieldA = a[sortField.value];
            fieldB = b[sortField.value];
        }

        if (fieldA < fieldB) return sortDirection.value === 'asc' ? -1 : 1;
        if (fieldA > fieldB) return sortDirection.value === 'asc' ? 1 : -1;
        return 0;
    });

    return result;
});

const paginatedLetters = computed(() => {
    const start = (currentPage.value - 1) * itemsPerPage.value;
    const end = start + itemsPerPage.value;
    return filteredLetters.value.slice(start, end);
});

const totalPages = computed(() => {
    return Math.ceil(filteredLetters.value.length / itemsPerPage.value);
});

// Data loading functions
const loadData = async () => {
    isLoading.value = true;
    error.value = null;

    try {
        const response = await axios.get(`${BASE_URL}/violation-actions/warning-letters/get`, {
            headers: {
                'Authorization': `Bearer ${store.state.token}`,
                'X-Company-ID': store.state.currentCompany?.id
            }
        });

        warningLetters.value = response.data.data || [];
    } catch (err) {
        error.value = err.message || 'Failed to load warning letters';
        showErrorAlert(error.value);
    } finally {
        isLoading.value = false;
    }
};

const loadEmployees = async () => {
    try {
        const response = await axios.get(`${BASE_URL_2}/employee/driver/show`);
        drivers.value = response.data.drivers || response.data.data || response.data;
    } catch (err) {
        console.error('Error fetching employees:', err);
        showErrorAlert('Failed to load drivers. Please try again later.');
    }
};

const loadTrucks = async () => {
    try {
        const response = await axios.get(`${BASE_URL_2}/vehicle/truck/show`);
        trucks.value = response.data.trucks || response.data.data || response.data;
    } catch (err) {
        console.error('Error fetching trucks:', err);
        showErrorAlert('Failed to load trucks. Please try again later.');
    }
};

const loadTrailers = async () => {
    try {
        const response = await axios.get(`${BASE_URL_2}/vehicle/trailer/show`);
        trailers.value = response.data.trailers || response.data.data || response.data;
    } catch (err) {
        console.error('Error fetching trailers:', err);
        showErrorAlert('Failed to load trailers. Please try again later.');
    }
};

// Helper functions
const getDriverName = (employeeId) => {
    const driver = drivers.value.find(d => d.employee_id == employeeId);
    return driver?.driver || 'N/A';
};

const formatDate = (dateString) => {
    if (!dateString) return '—';
    const options = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    };
    return new Date(dateString).toLocaleString(undefined, options);
};

const viewDetails = (id) => {
    router.push(`/violation/action/warning-letter-detail/${id}`);
};

const deleteWarningLetter = async (id) => {
    const confirmed = await showConfirmDialog(
        'Delete Warning Letter',
        'Are you sure you want to delete this warning letter? This action cannot be undone.'
    );

    if (!confirmed) return;

    try {
        isLoading.value = true;
        await axios.delete(`${BASE_URL}/violation-actions/warning-letters/${id}`, {
            headers: {
                'Authorization': `Bearer ${store.state.token}`,
                'X-Company-ID': store.state.currentCompany?.id
            }
        });

        showSuccessAlert('Warning letter deleted successfully!');
        await loadData();
    } catch (err) {
        showErrorAlert(err.response?.data?.message || 'Failed to delete warning letter');
    } finally {
        isLoading.value = false;
    }
};

const sortBy = (field) => {
    if (sortField.value === field) {
        sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
    } else {
        sortField.value = field;
        sortDirection.value = 'asc';
    }
};

const changePage = (page) => {
    currentPage.value = page;
};

const refreshData = async () => {
    await loadData();
    showSuccessAlert('Data refreshed successfully!');
};

onMounted(async () => {
    isLoading.value = true;
    error.value = null;

    try {
        await Promise.all([
            loadData(),
            loadTrucks(),
            loadTrailers(),
            loadEmployees()
        ]);
    } catch (err) {
        showErrorAlert(err.message || 'Failed to load initial data');
    } finally {
        isLoading.value = false;
    }
});
</script>

<template>
    <div class="layout-px-spacing">
        <div class="row layout-top-spacing">
            <div class="col-xl-12 col-lg-12 col-md-12 col-12 layout-spacing">
                <div class="panel br-4">
                    <div class="panel-body">
                        <div class="d-flex justify-content-between align-items-center mb-4">
                            <h3 class="mb-0">Driver With Warning Letters</h3>
                            <div class="d-flex">
                                <div class="input-group me-2" style="width: 300px;">
                                    <input type="text" class="form-control" placeholder="Search..."
                                        v-model="searchQuery" @keyup.enter="changePage(1)">
                                </div>
                                <button class="btn btn-primary" @click="refreshData" :disabled="isLoading">
                                    <i class="fas fa-sync-alt" :class="{ 'fa-spin': isLoading }"></i>
                                    Refresh
                                </button>
                            </div>
                        </div>

                        <div v-if="error" class="alert alert-danger">
                            {{ error }}
                        </div>

                        <div v-else-if="isLoading" class="text-center p-4">
                            <div class="spinner-border text-primary" role="status">
                                <span class="visually-hidden">Loading...</span>
                            </div>
                            <p class="mt-2">Loading warning letters...</p>
                        </div>

                        <div v-else-if="warningLetters.length === 0" class="alert alert-info">
                            No warning letters found.
                        </div>

                        <div v-else>
                            <div class="table-responsive">
                                <table class="table table-hover">
                                    <thead>
                                        <tr>
                                            <th class="text-center">S/N</th>
                                            <th @click="sortBy('employee_id')">
                                                Employee ID
                                                <i class="fas fa-sort ms-1"></i>
                                            </th>
                                            <th @click="sortBy('driver')">
                                                Driver
                                                <i class="fas fa-sort ms-1"></i>
                                            </th>
                                            <th @click="sortBy('created_at')">
                                                Issued On
                                                <i class="fas fa-sort ms-1"></i>
                                            </th>
                                            <th>Issued By</th>
                                            <th>Period</th>
                                            <th>Violations</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="(letter, index) in paginatedLetters" :key="letter.id">
                                            <td class="text-center">{{ (currentPage - 1) * itemsPerPage + index + 1 }}
                                            </td>
                                            <td>{{ letter.employee_id }}</td>
                                            <td>{{ getDriverName(letter.employee_id) || 'N/A' }}</td>
                                            <td>{{ formatDate(letter.created_at) }}</td>
                                            <td>{{ letter.issued_by?.username || 'N/A' }}</td>
                                            <td>
                                                {{ formatDate(letter.start_date) }} to
                                                {{ formatDate(letter.end_date) }}
                                            </td>
                                            <td>{{ letter.violation_ids?.length || 0 }}</td>
                                            <td>
                                                <div class="btn-group" role="group">
                                                    <button class="btn btn-md btn-primary"
                                                        @click="viewDetails(letter.id)" title="View Details">
                                                        View
                                                    </button>
                                                    <button class="btn btn-md btn-danger"
                                                        @click="deleteWarningLetter(letter.id)" title="Delete">
                                                        Delete
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            <div class="row mt-3" v-if="filteredLetters.length > itemsPerPage">
                                <div class="col-md-12">
                                    <nav aria-label="Page navigation">
                                        <ul class="pagination justify-content-center">
                                            <li class="page-item" :class="{ disabled: currentPage === 1 }">
                                                <button class="page-link" @click="changePage(currentPage - 1)">
                                                    Previous
                                                </button>
                                            </li>

                                            <li class="page-item" v-for="page in totalPages" :key="page"
                                                :class="{ active: currentPage === page }">
                                                <button class="page-link" @click="changePage(page)">
                                                    {{ page }}
                                                </button>
                                            </li>

                                            <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                                                <button class="page-link" @click="changePage(currentPage + 1)">
                                                    Next
                                                </button>
                                            </li>
                                        </ul>
                                    </nav>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.table th {
    background-color: #f8f9fa !important;
    cursor: pointer;
    user-select: none;
}

.table th:hover {
    background-color: #e9ecef !important;
}

.btn-group {
    white-space: nowrap;
}

.page-item.active .page-link {
    background-color: #3b3f5c;
    border-color: #3b3f5c;
}

.page-link {
    color: #3b3f5c;
}

.input-group {
    max-width: 300px;
}

.fa-sort {
    opacity: 0.5;
}

.fa-sort:hover {
    opacity: 1;
}

.fa-spin {
    animation: fa-spin 1s infinite linear;
}
</style>